import React, { useCallback, useMemo, useState } from 'react';

import { generateShortId } from '@lyearn/core/utils/id';
import { Autocomplete, Input } from '@lyearn/ui';
import { getRandomOptionColor } from '@/modules/corporate/modules/database/utils/metadataField';
import { SelectFieldOptionsType } from '@/types/schema';

import { renderOption } from './components/renderers';
import Tag from './components/Tag';
import getFilteredOptions, { ADD_OPTION_ID } from './utils/getFilteredOptions';
import getSelectedOptions from './utils/getSelectedOptions';
import getValue from './utils/getValue';
import { DatabaseSelectProps } from './types';

function DatabaseSelect(props: DatabaseSelectProps) {
  const [color, setColor] = useState(getRandomOptionColor());
  const {
    field,
    value,
    onChange,
    multiple = false,
    openOnFocus = true,
    className,
    classes,
    onAddOption,
    onClose,
  } = props;

  const { selectedOptions, options } = useMemo(() => {
    const selectOptions = field.metadataField.options || [];

    if (!value) {
      return { selectedOptions: [], options: selectOptions };
    }

    return { selectedOptions: getSelectedOptions(selectOptions, value), options: selectOptions };
  }, [field.metadataField.options, value]);

  const _onChange = useCallback(
    (_event: React.ChangeEvent<{}>, nextValue: SelectFieldOptionsType[]) => {
      let optionToAdd = nextValue.find((option) => option.optionId === ADD_OPTION_ID);

      if (optionToAdd) {
        optionToAdd = { ...optionToAdd, optionId: generateShortId() };
        onAddOption?.(optionToAdd);
        onChange(getValue([...selectedOptions, optionToAdd], multiple));
        setColor(getRandomOptionColor());
      } else {
        onChange(getValue(nextValue, multiple));
      }
    },
    [multiple, onAddOption, onChange, selectedOptions],
  );

  const onDelete = useCallback(
    (optionId: string) => {
      onChange(
        getValue(
          selectedOptions.filter((option) => option.optionId !== optionId),
          multiple,
        ),
      );
    },
    [multiple, onChange, selectedOptions],
  );

  return (
    <Autocomplete
      autoHighlight
      multiple
      className={className}
      classes={classes}
      filterOptions={onAddOption ? getFilteredOptions(color) : undefined}
      getOptionLabel={(option) => option.value}
      openOnFocus={openOnFocus}
      options={options}
      popupIcon={null}
      renderInput={(params) => <Input {...params} autoFocus />}
      renderOption={renderOption}
      renderTags={(values) =>
        values.map((option) => (
          <Tag key={option.optionId} option={option} onDelete={multiple ? onDelete : undefined} />
        ))
      }
      value={selectedOptions}
      onChange={_onChange}
      onClose={onClose}
    />
  );
}

export default DatabaseSelect;
